/**
 * GestureController.js
 * Универсальный контроллер жестов для игр.
 * Поддерживает:
 * - Тап (клик)
 * - Длительное нажатие
 * - Свайпы (дискретные и непрерывные)
 * - Определение зон тапа (верх/низ/лево/право)
 */

export default class GestureController {
  constructor(element, options = {}) {
    this.element = element;

    // Настройки по умолчанию
    this.options = {
      sensitivityX: options.sensitivityX || 20, // Пикселей для срабатывания по X
      sensitivityY: options.sensitivityY || 20, // Пикселей для срабатывания по Y
      continuous: options.continuous || false,  // Если true - срабатывает многократно при движении (Тетрис)
      disableDiagonal: options.disableDiagonal || true, // Блокировать диагонали (выбирать доминантную ось)
      longPressTime: 500, // мс для долгого тапа
      // НОВОЕ: список селекторов, на которых жесты не работают
      excludeSelectors: options.excludeSelectors || ['button', 'input', 'textarea', '[role="button"]'],

      // НОВОЕ: Блокировка оси. Если true, то после начала движения по одной оси,
      // движение по другой игнорируется до конца жеста.
      lockAxis: options.lockAxis || false,
      axisLockThreshold: options.axisLockThreshold || 10, // Сколько пикселей нужно пройти, чтобы залочить ось
      ...options
    };

    // Коллбеки
    this.onSwipe = options.onSwipe || (() => {}); // (direction, distance, swipeCount)
    this.onTap = options.onTap || (() => {});     // (zone: 'top'|'bottom'|'left'|'right')
    this.onLongPress = options.onLongPress || (() => {});

    // Внутреннее состояние
    this.startX = 0;
    this.startY = 0;
    this.lastX = 0;
    this.lastY = 0;

    // Аккумуляторы для непрерывного движения (Тетрис)
    this.accumX = 0;
    this.accumY = 0;

    this.isDragging = false;
    this.isCancelled = false; // <--- НОВЫЙ ФЛАГ
    this.startTime = 0;
    this.longPressTimer = null;
    this.hasMovedSignificant = false; // Было ли значимое движение (для отмены тапа)

    // НОВОЕ: Состояние заблокированной оси ('x', 'y' или null)
    this.lockedAxis = null;

    // НОВОЕ: Счетчик свайпов за одно касание
    this.swipeCount = 0;

    // Привязка контекста
    this.handleStart = this.handleStart.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleCancel = this.handleCancel.bind(this);

    this.attach();
  }

  attach() {
    // Используем Pointer Events для поддержки мыши и тача одновременно
    this.element.addEventListener('pointerdown', this.handleStart);
    this.element.addEventListener('pointermove', this.handleMove);
    this.element.addEventListener('pointerup', this.handleEnd);
    this.element.addEventListener('pointercancel', this.handleCancel);
    this.element.addEventListener('pointerleave', this.handleCancel);

    // Блокируем контекстное меню браузера при долгом тапе
    this.element.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  detach() {
    this.element.removeEventListener('pointerdown', this.handleStart);
    this.element.removeEventListener('pointermove', this.handleMove);
    this.element.removeEventListener('pointerup', this.handleEnd);
    this.element.removeEventListener('pointercancel', this.handleCancel);
    this.element.removeEventListener('pointerleave', this.handleCancel);
  }

  // <--- НОВЫЙ МЕТОД: Принудительная отмена текущего жеста
  cancelGesture() {
    this.isCancelled = true;
  }

  handleStart(e) {
    // Блокируем стандартное поведение (скролл и т.д.)
    // e.preventDefault(); // (Иногда мешает фокусу, лучше использовать touch-action: none в CSS)

    // НОВОЕ: Проверка на исключения
    // Если кликнули по кнопке управления - не запускаем жест
    if (this.options.excludeSelectors.length > 0) {
        if (e.target.closest(this.options.excludeSelectors.join(','))) {
            return;
        }
    }

    this.isDragging = true;
    this.isCancelled = false; // <--- Сброс флага при новом нажатии
    this.hasMovedSignificant = false;
    this.swipeCount = 0; // Сброс счетчика
    this.startX = e.clientX;
    this.startY = e.clientY;

    // Точка отсчета для свайпов
    this.lastX = e.clientX;
    this.lastY = e.clientY;

    this.accumX = 0;
    this.accumY = 0;

    this.startTime = Date.now();

    // Таймер долгого тапа
    this.longPressTimer = setTimeout(() => {
      // Если жест отменен, долгий тап тоже не должен сработать
      if (!this.hasMovedSignificant && this.isDragging && !this.isCancelled) {
        const zone = this.getZone(this.startX, this.startY);
        this.onLongPress(zone);
        this.hasMovedSignificant = true; // Считаем, что действие выполнено, чтобы не сработал Tap при отпускании
      }
    }, this.options.longPressTime);
  }

  handleMove(e) {
    if (!this.isDragging) return;
    if (this.isCancelled) return; // <--- Блокируем обработку, если жест отменен

    const currentX = e.clientX;
    const currentY = e.clientY;

    let deltaX = currentX - this.lastX;
    let deltaY = currentY - this.lastY;

    // --- ЛОГИКА LOCK AXIS ---
    if (this.options.lockAxis) {
        // Если ось еще не выбрана, проверяем общее смещение от старта
        if (!this.lockedAxis) {
            const totalDx = Math.abs(currentX - this.startX);
            const totalDy = Math.abs(currentY - this.startY);
            const threshold = this.options.axisLockThreshold;

            if (totalDx > threshold || totalDy > threshold) {
                // Если движение явно больше по одной оси, лочим её
                if (totalDx > totalDy) {
                    this.lockedAxis = 'x';
                } else {
                    this.lockedAxis = 'y';
                }
            }
        }

        // Если ось залочена, обнуляем дельту по другой оси
        if (this.lockedAxis === 'x') {
            deltaY = 0;
        } else if (this.lockedAxis === 'y') {
            deltaX = 0;
        }
    }
    // ------------------------

    // Общий сдвиг от начала касания (для проверки на "просто тап")
    const totalDist = Math.sqrt(Math.pow(currentX - this.startX, 2) + Math.pow(currentY - this.startY, 2));
    if (totalDist > 10) {
      this.hasMovedSignificant = true;
      if (this.longPressTimer) clearTimeout(this.longPressTimer);
    }

    // --- ЛОГИКА ЖЕСТОВ ---

    if (this.options.continuous) {
      // **Режим Тетриса**: Накапливаем дельту. Срабатываем каждый раз при превышении порога.
      this.accumX += deltaX;
      this.accumY += deltaY;

      // Обработка X
      while (Math.abs(this.accumX) >= this.options.sensitivityX) {
        this.swipeCount++; // +1 свайп
        const dir = this.accumX > 0 ? 'right' : 'left';
        this.onSwipe(dir, Math.abs(this.accumX), this.swipeCount);
        // Важно: если в onSwipe вызовется cancelGesture(), следующий цикл уже не должен иметь смысла,
        // но так как мы проверяем isCancelled в начале handleMove, достаточно прервать текущий цикл если надо.
        // Однако для простоты JS single thread просто обновим аккумуляторы.
        if (this.isCancelled) return; // Выход, если жест отменили внутри коллбека

        // Уменьшаем аккумулятор на величину порога (сохраняя остаток для плавности)
        this.accumX -= (this.accumX > 0 ? this.options.sensitivityX : -this.options.sensitivityX);
      }

      // Обработка Y
      while (Math.abs(this.accumY) >= this.options.sensitivityY) {
        this.swipeCount++; // +1 свайп
        const dir = this.accumY > 0 ? 'down' : 'up';
        this.onSwipe(dir, Math.abs(this.accumY), this.swipeCount);
        if (this.isCancelled) return;

        this.accumY -= (this.accumY > 0 ? this.options.sensitivityY : -this.options.sensitivityY);
      }

      // Обновляем lastX/lastY, так как мы "потребили" движение
      this.lastX = currentX;
      this.lastY = currentY;

    } else {
      // **Режим Змейки**: Умная дискретная регистрация.
      // Мы не сбрасываем anchor (lastX/Y) пока не пройдем порог.
      // Как только порог пройден - засчитываем жест и переносим anchor в текущую точку.
      // Это позволяет делать жесты "Вправо -> Вверх" не отрывая пальца.

      const diffX = currentX - this.lastX;
      const diffY = currentY - this.lastY;

      // Применяем лок к diff (хотя delta выше уже обнулена, но логика тут отдельная)
      const effectiveDiffX = (this.options.lockAxis && this.lockedAxis === 'y') ? 0 : diffX;
      const effectiveDiffY = (this.options.lockAxis && this.lockedAxis === 'x') ? 0 : diffY;

      // Проверяем, какая ось доминирует, если включена блокировка диагоналей
      if (Math.abs(effectiveDiffX) > this.options.sensitivityX &&
         (!this.options.disableDiagonal || Math.abs(effectiveDiffX) > Math.abs(effectiveDiffY))) {

        this.swipeCount++;
        const dir = effectiveDiffX > 0 ? 'right' : 'left';
        this.onSwipe(dir, Math.abs(effectiveDiffX), this.swipeCount);

        // Сброс "якоря" на текущую позицию.
        // Теперь отсчет следующего жеста (например поворота) пойдет от этой точки.
        this.lastX = currentX;
        this.lastY = currentY; // Y тоже обновляем, чтобы "сбросить" накопленный шум по Y
      }
      else if (Math.abs(effectiveDiffY) > this.options.sensitivityY) {

        this.swipeCount++;
        const dir = effectiveDiffY > 0 ? 'down' : 'up';
        this.onSwipe(dir, Math.abs(effectiveDiffY), this.swipeCount);

        this.lastY = currentY;
        this.lastX = currentX;
      }
    }
  }

  handleEnd(e) {
    if (this.longPressTimer) clearTimeout(this.longPressTimer);

    if (!this.isDragging) return;
    this.isDragging = false;
    this.lockedAxis = null;

    // Тап срабатывает только если жест не был отменен и не было сильного сдвига
    if (!this.hasMovedSignificant && !this.isCancelled) {
      const zone = this.getZone(this.startX, this.startY);
      this.onTap(zone);
    }
  }

  handleCancel() {
    if (this.longPressTimer) clearTimeout(this.longPressTimer);
    this.isDragging = false;
    this.lockedAxis = null;
  }

  /**
   * Определяет зону тапа относительно центра элемента.
   * Делит прямоугольник на 4 треугольника (верх, право, низ, лево) диагоналями.
   */
  getZone(x, y) {
    // Если прикреплены к body, считаем зоны от всего окна
    const rect = (this.element === document.body || this.element === document.documentElement)
      ? { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
      : this.element.getBoundingClientRect();

    // Координаты относительно центра элемента
    const localX = x - rect.left - rect.width / 2;
    const localY = y - rect.top - rect.height / 2; // Y растет вниз

    // Используем atan2 для определения угла.
    // -PI ... PI.
    // 0 - право, PI/2 - низ, PI/-PI - лево, -PI/2 - верх

    // Однако, для прямоугольных зон проще использовать неравенства диагоналей.
    // Нормализуем координаты по соотношению сторон, чтобы работать как с квадратом
    const normX = localX * (rect.height / rect.width);
    const normY = localY;

    if (Math.abs(normX) > Math.abs(normY)) {
      // Горизонталь доминирует
      return normX > 0 ? 'right' : 'left';
    } else {
      // Вертикаль доминирует
      return normY > 0 ? 'bottom' : 'top';
    }
  }

  destroy() {
    this.detach();
  }
}